import { SurfaceOutputsSchema, SurfaceTypeNodeSchema } from "./surface-declaration.schemas.js";
import { EXTENSION_POINT_DESCRIPTION_MAX_LENGTH, EXTENSION_POINT_ID_MAX_LENGTH, EXTENSION_POINT_ID_PATTERN, EXTENSION_POINT_KINDS, SLOT_FILL_KINDS, SURFACE_USE_APP_MAX_LENGTH, SURFACE_USE_APP_PATTERN } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/extension-point.schemas.ts
/**
* The answer of `GET /v1/extension-points/:owner/:name` on the release engine (surface extension
* points, D12, D13).
*
* The route answers one extension point as the CP registry holds it: its owner, the first and latest
* versions of the owner that declare it, and the declaration from two sources. The frozen part
* (`kind`, `fill`, `input`, `outputs`) is the first version's, which every later version repeats
* (D5). The mutable part (`description`, `deprecated`) is the latest version's, so an owner that
* marks the point deprecated in a later version changes the answer. `zion-app points pull <id>`
* parses the answer with this schema and writes zod parsers and a typed reference from it (D12).
* The answer carries the point row only, never the owner's whole `app.json`.
*
* Every field rule restates the contract in `@zion/sdk-contracts`: the id grammar and cap
* (`EXTENSION_POINT_ID_PATTERN`, `EXTENSION_POINT_ID_MAX_LENGTH`), the owner slug grammar
* (`SURFACE_USE_APP_PATTERN`, the `appId` grammar), the two kinds and the two fill kinds, the
* description cap, and the closed surface type language for `input` and `outputs` through
* {@link SurfaceTypeNodeSchema} and {@link SurfaceOutputsSchema}. The object is `.strict()`.
*
* Two cross-field rules hold here, because the CP builds the record from a declaration the publish
* door already checked:
* 1. `owner` equals the id prefix before the `/`. A record that breaks it fails at `['owner']`.
* 2. A `slot` carries `fill` and a `surface` point does not. A record that breaks it fails at
*    `['fill']`.
* Each rule runs only when the parse has no issue so far: every field parsed, and the rule before it
* held. So a refused record carries at most one cross-field issue, and never one beside a field
* fault. The refines are checks, not transforms, so the schema stays representable for
* `createOpenApiDocument()`.
*
* Pure: no I/O, no state.
*/
/** A refine runs only when the parse has raised no issue before it. */
function noIssueSoFar(payload) {
	return payload.issues.length === 0;
}
/**
* One extension point record.
*
* - `id`: the point id, `<owner>/<name>.vN`.
* - `owner`: the owner's registered slug, equal to the id prefix before the `/`.
* - `kind`: `slot` or `surface`.
* - `fill`: `item` or `surface` on a slot; absent on a surface point.
* - `input`, `outputs`: the declared schemas, absent when the declaration has none.
* - `description`: the text of the `latestVersion` row, at most
*   `EXTENSION_POINT_DESCRIPTION_MAX_LENGTH` characters; absent when that row has none.
* - `deprecated`: the flag of the `latestVersion` row, `false` when that row omits it. Always
*   present. `true` tells consumers to move to a later `.vN`.
* - `firstVersion`: the owner version that first declared the point. Its row gives `kind`,
*   `fill`, `input` and `outputs`.
* - `latestVersion`: the latest published owner version that still declares the point. Its row
*   gives `description` and `deprecated`. Equal to `firstVersion` when one version declares it.
*
* A key outside this list fails the parse. The inferred type is the record's only type; a consumer
* writes `z.infer<typeof ExtensionPointRecordSchema>`.
*/
const ExtensionPointRecordSchema = z.object({
	id: z.string().max(EXTENSION_POINT_ID_MAX_LENGTH).regex(new RegExp(EXTENSION_POINT_ID_PATTERN)),
	owner: z.string().max(SURFACE_USE_APP_MAX_LENGTH).regex(new RegExp(SURFACE_USE_APP_PATTERN)),
	kind: z.enum(EXTENSION_POINT_KINDS),
	fill: z.enum(SLOT_FILL_KINDS).optional(),
	input: SurfaceTypeNodeSchema.optional(),
	outputs: SurfaceOutputsSchema.optional(),
	description: z.string().max(EXTENSION_POINT_DESCRIPTION_MAX_LENGTH).optional(),
	deprecated: z.boolean(),
	firstVersion: z.string().min(1),
	latestVersion: z.string().min(1)
}).strict().refine((record) => record.id.slice(0, record.id.indexOf("/")) === record.owner, {
	message: "owner must equal the id prefix",
	path: ["owner"],
	when: noIssueSoFar
}).refine((record) => record.kind === "slot" === (record.fill !== void 0), {
	message: "a slot carries fill and a surface point does not",
	path: ["fill"],
	when: noIssueSoFar
});
//#endregion
export { ExtensionPointRecordSchema };

//# sourceMappingURL=extension-point.schemas.js.map